import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Loader2, CheckCircle2, FileVideo, Calendar as CalendarIcon, ChevronLeft, ChevronRight,
  ArrowRight, ClipboardList, MessageSquare, Send, Video, Clock, Users, Clapperboard,
  Home, ListTodo, CheckSquare, Download, FileText, Receipt, Upload, Trash2, Link2, FileBarChart,
  Megaphone, TrendingUp, Target, Eye, MousePointerClick,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { contentCalendarAPI, tasksAPI, clientPortalAPI, serviceDeliveryAPI, briefingFileUrl, clientWelcomeAPI } from '../lib/api';
import { ClientAdsApprovals } from './ClientAdsApprovals';
import { AdApprovalListCard, AdApprovalReview } from './AdApprovalParts';
import BriefingForm from './BriefingForm';
import { getStateMeta, APPROVAL_PENDING_STATE } from '../lib/contentStates';
import { ChannelLabels } from './ChannelIcons';
import ApprovalReview, { ApprovalListCard } from './ApprovalReview';
import { FeedGrid } from './InstagramPreview';
import { useAuthStore } from '../store';
import { toast } from 'sonner';
import ClientContentCalendar from './ClientContentCalendar';
import ClientReportsSection from './ClientReportsSection';
import ClientDriveFolders, { useClientDriveFolders } from './ClientDriveFolders';
import ClientDocUpload from './ClientDocUpload';
import ClientPlanFiles from './ClientPlanFiles';
import { isClientViewMode, adsApprovalsAPI } from '../lib/api';
import { FolderOpen } from 'lucide-react';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { StaleDataNote } from './StaleDataNote';

const SPANISH_MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

function formatDate(s) {
  if (!s) return '';
  const d = new Date(s + 'T00:00:00');
  if (isNaN(d.getTime())) return s;
  return d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}
function fmtObsDate(s) {
  if (!s) return '';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function StateBadge({ stateId }) {
  const meta = getStateMeta(stateId);
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium" style={{ backgroundColor: meta.bg, color: meta.fg }}>
      {meta.label}
    </span>
  );
}

function ObservationsThread({ observaciones }) {
  if (!observaciones || observaciones.length === 0) return null;
  return (
    <div className="rounded-md border bg-muted/20 p-3 space-y-2" data-testid="observations-thread">
      <p className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5"><MessageSquare className="w-3.5 h-3.5" /> Historial de observaciones</p>
      <div className="space-y-2 max-h-52 overflow-y-auto">
        {observaciones.map((o) => {
          const isClient = o.autor_tipo === 'cliente';
          return (
            <div key={o.id} className={`text-sm rounded-md px-2.5 py-1.5 border ${isClient ? 'bg-amber-50 border-amber-200' : 'bg-sky-50 border-sky-200'}`}>
              <div className="flex items-center justify-between gap-2 mb-0.5">
                <span className={`text-[11px] font-semibold ${isClient ? 'text-amber-800' : 'text-sky-800'}`}>{o.autor_nombre || (isClient ? 'Cliente' : 'Equipo')}</span>
                <span className="text-[10px] text-muted-foreground">{fmtObsDate(o.created_at)}</span>
              </div>
              <p className="whitespace-pre-wrap text-foreground/90">{o.texto}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PostCard({ post, actionable, onApprove, onRequestChanges, approving, cardRef, highlight }) {
  const attachments = post.attachments || [];
  const firstImage = attachments.find((a) => (a.kind || (a.content_type || '').split('/')[0]) === 'image');
  return (
    <Card ref={cardRef} className={`${actionable ? 'border-amber-300 ring-1 ring-amber-200' : ''} ${highlight ? 'ring-2 ring-primary shadow-lg' : ''} transition-all`} data-testid={`client-post-${post.id}`}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <CardTitle className="text-base truncate">{post.titulo || 'Sin título'}</CardTitle>
            <p className="text-xs text-muted-foreground capitalize mt-0.5 flex flex-wrap items-center gap-1">{formatDate(post.fecha)} · {post.tipo || 'post'} · <ChannelLabels post={post} emptyText="red social" testId={`client-post-channels-${post.id}`} /></p>
          </div>
          <StateBadge stateId={post.estado} />
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {firstImage ? (
          <img src={contentCalendarAPI.getAttachmentUrl(post.id, firstImage.file_id)} alt={firstImage.name || ''} className="w-full h-48 object-cover rounded-md border" />
        ) : attachments.length > 0 ? (
          <div className="w-full h-32 flex items-center justify-center bg-muted rounded-md border"><FileVideo className="w-10 h-10 text-muted-foreground" /></div>
        ) : null}
        {post.copy && (
          <div className="text-sm text-foreground whitespace-pre-wrap line-clamp-4 bg-muted/30 rounded-md p-3 border" data-testid={`client-copy-${post.id}`}>{post.copy}</div>
        )}
        <ObservationsThread observaciones={post.observaciones} />
        {actionable && !isClientViewMode() && (
          <div className="grid grid-cols-2 gap-2">
            <Button onClick={() => onApprove(post)} disabled={approving === post.id} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`client-approve-${post.id}`}>
              {approving === post.id ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Aprobando...</>) : (<><CheckCircle2 className="w-4 h-4 mr-2" /> Aprobar</>)}
            </Button>
            <Button variant="outline" onClick={() => onRequestChanges(post)} className="border-amber-300 text-amber-700 hover:bg-amber-50" data-testid={`client-request-changes-${post.id}`}>
              <MessageSquare className="w-4 h-4 mr-2" /> Solicitar cambios
            </Button>
          </div>
        )}
        {post.estado === 'cambios_solicitados' && (
          <p className="text-xs text-orange-700 font-medium flex items-center gap-1.5" data-testid={`client-changes-pending-${post.id}`}><MessageSquare className="w-4 h-4" /> Cambios solicitados · el equipo está trabajando en ello</p>
        )}
        {!actionable && post.aprobado_cliente && (
          <p className="text-xs text-emerald-700 font-medium flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Aprobada por ti</p>
        )}
      </CardContent>
    </Card>
  );
}

export default function ClientPortalPage({ section = 'home' }) {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const isTeamViewer = !!user?.role && user.role !== 'client';

  useEffect(() => {
    if (section !== 'home' || isTeamViewer || sessionStorage.getItem(`welcome-shown-${user?.id}`)) return;
    sessionStorage.setItem(`welcome-shown-${user?.id}`, '1');
    clientWelcomeAPI.me().then((w) => { if (!w.all_done) navigate('/client-portal/welcome', { replace: true }); }).catch(() => {});
  }, [section, isTeamViewer, navigate, user?.id]);

  const [posts, setPosts] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(null);
  const [confirmPost, setConfirmPost] = useState(null);
  const [changesPost, setChangesPost] = useState(null);
  const [changesText, setChangesText] = useState('');
  const [submittingChanges, setSubmittingChanges] = useState(false);
  const [highlightId, setHighlightId] = useState(null);
  const [reviewId, setReviewId] = useState(null);
  const [bulkSel, setBulkSel] = useState(() => new Set());
  const [approvalsTab, setApprovalsTab] = useState('pending');
  const [igProfile, setIgProfile] = useState(null);
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const cardRefs = useRef({});
  const [filterMonth, setFilterMonth] = useState(() => { const n = new Date(); return { year: n.getFullYear(), month: n.getMonth() }; });

  // briefing
  const [briefingData, setBriefingData] = useState(null);
  const [briefingPayment, setBriefingPayment] = useState('');
  const [briefingStatus, setBriefingStatus] = useState('pendiente');
  const [clientsList, setClientsList] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');

  // documentos / informes / facturas
  const [docs, setDocs] = useState(null);
  const [invoices, setInvoices] = useState(null);
  const [invLinked, setInvLinked] = useState(true);
  const [quipuContacts, setQuipuContacts] = useState([]);
  const [linkedContactId, setLinkedContactId] = useState('');
  const [upFile, setUpFile] = useState(null);
  const [upNombre, setUpNombre] = useState('');
  const [upMes, setUpMes] = useState('');
  const [uploading, setUploading] = useState(false);

  // campañas (Meta)
  const [camp, setCamp] = useState(null);
  const [campMonth, setCampMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [campHist, setCampHist] = useState(null);

  const adminClientId = isTeamViewer ? selectedClientId : null;
  const docCategoria = section === 'reports' ? 'informe' : 'documento';

  // Cargar lista de clientes (vista equipo) para las secciones que la usan
  useEffect(() => {
    if (!isTeamViewer) return;
    if (!['briefing', 'documents', 'reports', 'invoices'].includes(section)) return;
    if (clientsList.length === 0) serviceDeliveryAPI.getClients().then((cs) => setClientsList(Array.isArray(cs) ? cs : [])).catch(() => {});
  }, [section, isTeamViewer, clientsList.length]);

  // Documentos / Informes
  const loadDocs = useCallback(async () => {
    setDocs(null);
    try {
      if (isTeamViewer) {
        if (!selectedClientId) { setDocs([]); return; }
        const res = await serviceDeliveryAPI.listClientDocuments(selectedClientId);
        setDocs((res.documents || []).filter((d) => d.categoria === docCategoria));
      } else {
        const res = await clientPortalAPI.getDocuments(docCategoria);
        setDocs(res.documents || []);
      }
    } catch (e) { setDocs([]); }
  }, [isTeamViewer, selectedClientId, docCategoria]);
  useEffect(() => {
    if (section === 'documents' || (section === 'reports' && isTeamViewer)) loadDocs();
  }, [section, isTeamViewer, loadDocs]);

  // Facturas
  useEffect(() => {
    if (section !== 'invoices') return;
    if (isTeamViewer) {
      if (quipuContacts.length === 0) serviceDeliveryAPI.getAllQuipuContacts().then((r) => setQuipuContacts((r?.data) || [])).catch(() => {});
      if (selectedClientId) serviceDeliveryAPI.getClient(selectedClientId).then((c) => setLinkedContactId(c?.quipu_contact_id || '')).catch(() => {});
      setInvoices([]);
    } else {
      setInvoices(null);
      clientPortalAPI.getInvoices().then((r) => { setInvoices(r.invoices || []); setInvLinked(r.linked !== false); }).catch(() => { setInvoices([]); setInvLinked(false); });
    }
  }, [section, isTeamViewer, selectedClientId, quipuContacts.length]);

  // Campañas (Meta) — solo cliente
  useEffect(() => {
    if (section !== 'campaigns' || isTeamViewer) return;
    setCamp(null);
    clientPortalAPI.getCampaigns(campMonth)
      .then((r) => setCamp(r || { linked: false, connected: false, campaigns: [] }))
      .catch(() => setCamp({ linked: false, connected: false, campaigns: [] }));
  }, [section, isTeamViewer, campMonth]);

  // Evolución mensual (no depende del mes seleccionado)
  useEffect(() => {
    if (section !== 'campaigns' || isTeamViewer) return;
    setCampHist(null);
    clientPortalAPI.getCampaignsHistory(6)
      .then((r) => setCampHist(r || { connected: false, months: [] }))
      .catch(() => setCampHist({ connected: false, months: [] }));
  }, [section, isTeamViewer]);

  const [pendingAds, setPendingAds] = useState([]);
  const [adReviewId, setAdReviewId] = useState(null);
  const loadPendingAds = useCallback(() => adsApprovalsAPI.pending().then((r) => setPendingAds(r.creatives || [])).catch(() => setPendingAds([])), []);
  useEffect(() => {
    if (isTeamViewer || !['approvals', 'home'].includes(section)) return;
    loadPendingAds();
  }, [section, isTeamViewer, loadPendingAds]);
  const adInReview = pendingAds.find((c) => c.id === adReviewId);

  const doUpload = async () => {
    if (!adminClientId) { toast.error('Selecciona un cliente'); return; }
    if (!upFile) { toast.error('Elige un archivo'); return; }
    setUploading(true);
    try {
      await serviceDeliveryAPI.uploadClientDocument(adminClientId, { file: upFile, nombre: upNombre || upFile.name, categoria: docCategoria, mes: upMes });
      toast.success(docCategoria === 'informe' ? 'Informe subido' : 'Documento subido');
      setUpFile(null); setUpNombre(''); setUpMes('');
      loadDocs();
    } catch (e) { toast.error(e.message || 'Error subiendo'); }
    setUploading(false);
  };
  const doDeleteDoc = async (id) => {
    if (!window.confirm('¿Eliminar este archivo?')) return;
    try { await serviceDeliveryAPI.deleteClientDocument(adminClientId, id); toast.success('Eliminado'); loadDocs(); }
    catch (e) { toast.error(e.message); }
  };
  const doLinkQuipu = async (contactId) => {
    setLinkedContactId(contactId);
    try { await serviceDeliveryAPI.setQuipuContact(selectedClientId, contactId); toast.success('Contacto de Quipu vinculado'); }
    catch (e) { toast.error(e.message); }
  };

  const loadCore = useCallback(async () => {
    setLoading(true);
    try {
      const [postsData, tasksData, meetingsData] = await Promise.all([
        contentCalendarAPI.getAll().catch(() => []),
        tasksAPI.getMine().catch(() => []),
        clientPortalAPI.getMeetings().catch(() => []),
      ]);
      setPosts(Array.isArray(postsData) ? postsData : []);
      setTasks(Array.isArray(tasksData) ? tasksData : []);
      setMeetings(Array.isArray(meetingsData) ? meetingsData : []);
    } catch (e) {
      toast.error('Error cargando tu portal');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (!isTeamViewer) loadCore(); else setLoading(false); }, [loadCore, isTeamViewer]);

  // Briefing load
  const loadBriefing = useCallback(async () => {
    try {
      const res = await clientPortalAPI.getBriefing();
      setBriefingData(res?.briefing_data || {});
      setBriefingPayment(res?.forma_pago || '');
      setBriefingStatus(res?.briefing_status || 'pendiente');
    } catch (e) { toast.error('Error cargando el briefing'); }
  }, []);
  useEffect(() => {
    if (section !== 'briefing' && section !== 'home') return;
    if (isTeamViewer) {
      if (clientsList.length === 0) serviceDeliveryAPI.getClients().then((cs) => setClientsList(Array.isArray(cs) ? cs : [])).catch(() => {});
    } else if (briefingData === null) {
      loadBriefing();
    }
  }, [section, isTeamViewer, clientsList.length, briefingData, loadBriefing]);
  useEffect(() => {
    if (section === 'briefing' && isTeamViewer && selectedClientId) {
      setBriefingData(null);
      serviceDeliveryAPI.getClient(selectedClientId)
        .then((c) => { setBriefingData(c?.briefing_data || {}); setBriefingPayment(c?.forma_pago || ''); setBriefingStatus(c?.briefing_status || 'pendiente'); })
        .catch(() => toast.error('Error cargando el briefing'));
    }
  }, [section, isTeamViewer, selectedClientId]);

  const openPost = useCallback((post) => {
    if (!post) return;
    const ymd = (post.fecha || '').slice(0, 10);
    if (ymd) { const [y, m] = ymd.split('-'); setFilterMonth({ year: parseInt(y, 10), month: parseInt(m, 10) - 1 }); }
    setHighlightId(post.id);
    setTimeout(() => { const el = cardRefs.current[post.id]; if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, 150);
  }, []);

  useEffect(() => {
    const pid = searchParams.get('post');
    if (pid && posts.length) {
      const post = posts.find((p) => p.id === pid);
      if (post && section === 'approvals' && post.estado === APPROVAL_PENDING_STATE) { setReviewId(post.id); searchParams.delete('post'); setSearchParams(searchParams, { replace: true }); return; }
      if (post) { openPost(post); searchParams.delete('post'); setSearchParams(searchParams, { replace: true }); }
    }
  }, [posts, searchParams, setSearchParams, openPost, section]);

  const handleApprove = async (post) => {
    if (!post) return;
    setApproving(post.id);
    try {
      const updated = await contentCalendarAPI.approve(post.id);
      setPosts((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      tasksAPI.getMine().then((t) => setTasks(Array.isArray(t) ? t : [])).catch(() => {});
      toast.success('Publicación aprobada · enviada a programación');
    } catch (e) { toast.error(e?.message || 'Error aprobando'); }
    finally { setApproving(null); setConfirmPost(null); }
  };

  const handleRequestChanges = async () => {
    const post = changesPost; const texto = changesText.trim();
    if (!post || !texto) return;
    setSubmittingChanges(true);
    try {
      const updated = await contentCalendarAPI.requestChanges(post.id, texto);
      setPosts((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      toast.success('Cambios solicitados · el equipo lo revisará');
      setChangesPost(null); setChangesText('');
    } catch (e) { toast.error(e?.message || 'Error solicitando cambios'); }
    finally { setSubmittingChanges(false); }
  };

  useEffect(() => {
    if (isTeamViewer) return;
    clientPortalAPI.getProfile().then(setIgProfile).catch(() => {});
  }, [isTeamViewer]);
  const pendingApprovals = useMemo(() => posts.filter((p) => p.estado === APPROVAL_PENDING_STATE), [posts]);
  // Mis Tareas: tareas del cliente que NO son de aprobación (sin content_id)
  const myTasks = useMemo(() => tasks.filter((t) => !t.content_id && t.status !== 'completed'), [tasks]);
  const visiblePosts = useMemo(() => {
    const ymPrefix = `${filterMonth.year}-${String(filterMonth.month + 1).padStart(2, '0')}`;
    return posts.filter((p) => (p.fecha || '').startsWith(ymPrefix)).sort((a, b) => (a.fecha || '').localeCompare(b.fecha || ''));
  }, [posts, filterMonth]);

  // Si el mes actual no tiene publicaciones pero el cliente sí tiene (en otros meses),
  // saltamos automáticamente al mes con la publicación más cercana a hoy (una sola vez).
  const monthAutoSet = useRef(false);
  useEffect(() => {
    if (isTeamViewer || monthAutoSet.current || posts.length === 0) return;
    const ymPrefix = `${filterMonth.year}-${String(filterMonth.month + 1).padStart(2, '0')}`;
    const hasCurrent = posts.some((p) => (p.fecha || '').startsWith(ymPrefix));
    if (!hasCurrent) {
      const now = new Date();
      const dated = posts.filter((p) => p.fecha).map((p) => new Date(p.fecha)).filter((d) => !isNaN(d));
      if (dated.length) {
        dated.sort((a, b) => Math.abs(a - now) - Math.abs(b - now));
        const d = dated[0];
        setFilterMonth({ year: d.getFullYear(), month: d.getMonth() });
      }
    }
    monthAutoSet.current = true;
  }, [posts, isTeamViewer, filterMonth]);

  const nextMeeting = useMemo(() => {
    const now = Date.now();
    return [...meetings].filter((m) => new Date(m.start_datetime).getTime() >= now).sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime))[0];
  }, [meetings]);
  const nextPost = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return [...posts].filter((p) => (p.fecha || '') >= today).sort((a, b) => (a.fecha || '').localeCompare(b.fecha || ''))[0];
  }, [posts]);

  const goPrev = () => setFilterMonth((c) => (c.month === 0 ? { year: c.year - 1, month: 11 } : { year: c.year, month: c.month - 1 }));
  const goNext = () => setFilterMonth((c) => (c.month === 11 ? { year: c.year + 1, month: 0 } : { year: c.year, month: c.month + 1 }));

  if (loading) return <div className="py-20 text-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;

  const monthNav = (
    <div className="flex items-center gap-2">
      <Button variant="outline" size="sm" onClick={goPrev} data-testid="client-month-prev"><ChevronLeft className="w-4 h-4" /></Button>
      <div className="px-3 py-1.5 rounded-md bg-muted text-sm font-medium min-w-[160px] text-center" data-testid="client-month-label">{SPANISH_MONTHS[filterMonth.month]} {filterMonth.year}</div>
      <Button variant="outline" size="sm" onClick={goNext} data-testid="client-month-next"><ChevronRight className="w-4 h-4" /></Button>
    </div>
  );

  const notReady = (title) => (
    <Card><CardContent className="py-16 text-center text-muted-foreground text-sm" data-testid="section-coming-soon">
      La sección «{title}» estará disponible muy pronto.
    </CardContent></Card>
  );

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-5 max-w-5xl mx-auto" data-testid={`client-portal-${section}`}>

      {/* ===== MI PORTAL (inicio) ===== */}
      {section === 'home' && (
        <>
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2"><Home className="w-7 h-7 text-primary" /> Mi Portal</h1>
            <p className="text-sm text-muted-foreground mt-1">Resumen de tu cuenta con {user?.organization_name || 'tu agencia'}.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card className={pendingApprovals.length + pendingAds.length ? 'border-amber-300 bg-amber-50/50 cursor-pointer' : 'cursor-pointer'} onClick={() => navigate('/client-portal/approvals')} data-testid="home-approvals-card">
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Aprobaciones pendientes</p>
                <p className="text-2xl font-bold text-primary mt-1">{pendingApprovals.length + pendingAds.length}</p>
                <p className="text-xs text-primary mt-1 flex items-center gap-1">Ir a Aprobaciones <ArrowRight className="w-3 h-3" /></p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Próxima publicación</p>
                {nextPost ? (<><p className="text-sm font-semibold mt-1 truncate">{nextPost.titulo || 'Publicación'}</p><p className="text-xs text-muted-foreground capitalize">{formatDate(nextPost.fecha)}</p></>) : <p className="text-sm text-muted-foreground mt-2">Sin publicaciones próximas</p>}
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Próxima reunión</p>
                {nextMeeting ? (<><p className="text-sm font-semibold mt-1 truncate">{nextMeeting.titulo}</p><p className="text-xs text-muted-foreground">{new Date(nextMeeting.start_datetime).toLocaleString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p></>) : <p className="text-sm text-muted-foreground mt-2">Sin reuniones agendadas</p>}
              </CardContent>
            </Card>
          </div>
          {!isTeamViewer && <HomeDriveCard onOpen={() => navigate('/client-portal/documents')} />}
          <Card>
            <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3"><ClipboardList className="w-5 h-5 text-primary" /><div><p className="font-medium text-sm">Briefing de tu marca</p><p className="text-xs text-muted-foreground">Estado: {briefingStatus === 'completado' ? 'Completado' : briefingStatus === 'en_proceso' ? 'En proceso' : 'Pendiente'}</p></div></div>
              <Button variant="outline" size="sm" onClick={() => navigate('/client-portal/briefing')} data-testid="home-briefing-btn">Abrir Briefing</Button>
            </CardContent>
          </Card>
        </>
      )}

      {/* ===== APROBACIONES (sección única de acción sobre contenido) ===== */}
      {section === 'approvals' && (
        <>
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2"><CheckSquare className="w-7 h-7 text-primary" /> Aprobaciones</h1>
            <p className="text-sm text-muted-foreground mt-1">Todo lo que espera tu respuesta: publicaciones de redes y anuncios. Revisa cada uno y después apruébalo o solicita cambios.</p>
          </div>
          {!reviewId && !adReviewId && (
            <div className="flex rounded-lg border bg-background p-0.5 w-fit text-sm" data-testid="approvals-tabs">
              <button onClick={() => setApprovalsTab('pending')} className={`px-4 py-1.5 rounded-md font-medium transition-colors ${approvalsTab === 'pending' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`} data-testid="approvals-tab-pending">Pendientes ({pendingApprovals.length + pendingAds.length})</button>
              <button onClick={() => setApprovalsTab('feed')} className={`px-4 py-1.5 rounded-md font-medium transition-colors ${approvalsTab === 'feed' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`} data-testid="approvals-tab-feed">Mi feed</button>
            </div>
          )}
          {approvalsTab === 'feed' && !reviewId && !adReviewId ? (
            <FeedGrid posts={posts} profile={igProfile} onOpenPending={(p) => { setReviewId(p.id); }} />
          ) : adInReview ? (
            <AdApprovalReview c={adInReview} onBack={() => setAdReviewId(null)} onDone={() => { setAdReviewId(null); loadPendingAds(); }} />
          ) : pendingApprovals.length + pendingAds.length === 0 ? (
            <Card><CardContent className="py-16 text-center text-muted-foreground text-sm" data-testid="approvals-empty">No tienes nada pendiente de aprobar. Te avisaremos cuando haya novedades.</CardContent></Card>
          ) : reviewId ? (
            <ApprovalReview isClient={!isTeamViewer} profile={igProfile} pending={pendingApprovals} reviewId={reviewId} setReviewId={setReviewId}
              approving={approving} onApprove={(p) => setConfirmPost(p)} ObservationsThread={ObservationsThread}
              onUpdated={(updated) => setPosts((prev) => prev.map((p) => (p.id === updated.id ? updated : p)))} />
          ) : (
            <>
              {pendingAds.length > 0 && (
                <div className="space-y-2" data-testid="approvals-ads-list">
                  <p className="text-sm font-medium" data-testid="approvals-ads-count">{pendingAds.length} {pendingAds.length === 1 ? 'anuncio pendiente' : 'anuncios pendientes'} · pulsa en uno para verlo completo</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {pendingAds.map((c) => <AdApprovalListCard key={c.id} c={c} onOpen={setAdReviewId} />)}
                  </div>
                </div>
              )}
              {pendingApprovals.length > 0 && (<>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <p className="text-sm font-medium" data-testid="approvals-count">{pendingApprovals.length} {pendingApprovals.length === 1 ? 'publicación pendiente' : 'publicaciones pendientes'} · pulsa en una para revisarla</p>
                {!isTeamViewer && (() => {
                  const viewedIds = pendingApprovals.filter((p) => p.client_viewed_at).map((p) => p.id);
                  const sel = [...bulkSel].filter((id) => viewedIds.includes(id));
                  return (
                    <div className="flex items-center gap-2" data-testid="approvals-bulk-bar">
                      <Button variant="outline" size="sm" disabled={!viewedIds.length}
                        onClick={() => setBulkSel(sel.length === viewedIds.length ? new Set() : new Set(viewedIds))} data-testid="approvals-select-all">
                        {sel.length === viewedIds.length && viewedIds.length ? 'Quitar selección' : `Seleccionar todas las revisadas (${viewedIds.length})`}
                      </Button>
                      <Button size="sm" disabled={!sel.length} onClick={() => setBulkConfirm(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="approvals-bulk-approve">
                        <CheckCircle2 className="w-4 h-4 mr-1.5" /> Aprobar seleccionadas ({sel.length})
                      </Button>
                    </div>
                  );
                })()}
              </div>
              {!isTeamViewer && pendingApprovals.some((p) => !p.client_viewed_at) && (
                <p className="text-xs text-muted-foreground" data-testid="approvals-unviewed-hint">Las publicaciones «Sin revisar» hay que abrirlas antes de poder aprobarlas en bloque.</p>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {pendingApprovals.map((post) => (
                  <ApprovalListCard key={post.id} post={post} onOpen={(p) => setReviewId(p.id)} selectable={!isTeamViewer}
                    selected={bulkSel.has(post.id) && !!post.client_viewed_at}
                    onToggle={(id) => setBulkSel((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; })} />
                ))}
              </div>
              <AlertDialog open={bulkConfirm} onOpenChange={(o) => !bulkBusy && setBulkConfirm(o)}>
                <AlertDialogContent data-testid="approvals-bulk-confirm">
                  <AlertDialogHeader>
                    <AlertDialogTitle>¿Aprobar {[...bulkSel].filter((id) => pendingApprovals.some((p) => p.id === id && p.client_viewed_at)).length} publicaciones?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Pasarán a programarse con el contenido que has revisado:
                      <span className="block mt-2 space-y-0.5">
                        {pendingApprovals.filter((p) => bulkSel.has(p.id) && p.client_viewed_at).map((p) => (
                          <span key={p.id} className="block text-foreground">• {p.titulo || 'Sin título'} <span className="text-muted-foreground">({p.fecha ? new Date(`${p.fecha.slice(0, 10)}T12:00:00`).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' }) : 'sin fecha'})</span></span>
                        ))}
                      </span>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel disabled={bulkBusy} data-testid="approvals-bulk-cancel">Cancelar</AlertDialogCancel>
                    <AlertDialogAction disabled={bulkBusy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="approvals-bulk-confirm-btn"
                      onClick={async (e) => {
                        e.preventDefault();
                        setBulkBusy(true);
                        try {
                          const ids = pendingApprovals.filter((p) => bulkSel.has(p.id) && p.client_viewed_at).map((p) => p.id);
                          const r = await contentCalendarAPI.bulkApprove(ids);
                          const byId = Object.fromEntries((r.approved || []).map((u) => [u.id, u]));
                          setPosts((prev) => prev.map((p) => byId[p.id] || p));
                          toast.success(`${(r.approved || []).length} publicaciones aprobadas · ¡gracias!`);
                          if ((r.skipped || []).length) toast.warning(`${r.skipped.length} no se pudieron aprobar (revísalas de nuevo)`);
                          setBulkSel(new Set()); setBulkConfirm(false);
                        } catch (err) { toast.error(err?.message || 'Error al aprobar'); }
                        finally { setBulkBusy(false); }
                      }}>
                      {bulkBusy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />} Sí, aprobar
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              </>)}
            </>
          )}
        </>
      )}

      {/* ===== MIS TAREAS (sin aprobaciones) ===== */}
      {section === 'tasks' && (
        <>
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2"><ListTodo className="w-7 h-7 text-primary" /> Mis Tareas</h1>
            <p className="text-sm text-muted-foreground mt-1">Tareas pendientes de tu parte. Las aprobaciones de contenido están en la sección «Aprobaciones».</p>
          </div>
          {myTasks.length === 0 ? (
            <Card><CardContent className="py-16 text-center text-muted-foreground text-sm" data-testid="tasks-empty">No tienes tareas pendientes ahora mismo.</CardContent></Card>
          ) : (
            <Card><CardContent className="p-4 space-y-2">
              {myTasks.map((task) => (
                <div key={task.id} className="flex items-center gap-3 p-3 rounded-lg border" data-testid={`client-task-${task.id}`}>
                  <CheckCircle2 className="w-4 h-4 text-muted-foreground shrink-0" />
                  <span className="text-sm font-medium">{task.title}</span>
                </div>
              ))}
            </CardContent></Card>
          )}
        </>
      )}

      {/* ===== CALENDARIO (solo lectura: publicaciones + reuniones) ===== */}
      {section === 'calendar' && (
        <>
          <ClientContentCalendar
            clientId={null}
            initialPostId={highlightId}
            onInitialPostOpened={() => setHighlightId(null)}
          />
          <Card data-testid="client-meetings-portal">
            <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><Video className="w-5 h-5 text-primary" /> Tus reuniones y grabaciones</CardTitle></CardHeader>
            <CardContent>
              {meetings.length === 0 ? (
                <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground" data-testid="portal-meetings-empty">Aún no tienes reuniones agendadas.</div>
              ) : (
                <div className="space-y-3">
                  {meetings.map((m) => {
                    const d = new Date(m.start_datetime);
                    const when = isNaN(d) ? m.start_datetime : d.toLocaleString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
                    const isRec = m.tipo === 'grabacion';
                    return (
                      <div key={m.id} className="rounded-lg border p-4" data-testid={`portal-meeting-${m.id}`}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium">{m.titulo}</p>
                          {isRec && <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-purple-100 text-purple-800"><Clapperboard className="w-3 h-3" /> Grabación</span>}
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {when}</span>
                          {m.participantes?.length > 0 && <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {m.participantes.join(', ')}</span>}
                        </div>
                        {m.meet_link && (<a href={m.meet_link} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline" data-testid={`portal-meeting-link-${m.id}`}><Video className="w-4 h-4" /> Unirse a la videollamada</a>)}
                        {m.notas && <p className="mt-2 text-xs text-muted-foreground whitespace-pre-wrap">{m.notas}</p>}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* ===== BRIEFING ===== */}
      {section === 'briefing' && (
        <Card data-testid="client-briefing-section">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><ClipboardList className="w-5 h-5 text-primary" /> Briefing de tu marca</CardTitle>
            <p className="text-sm text-muted-foreground">Rellena la información de tu marca. Puedes guardar cada sección e ir completándolo poco a poco.</p>
            {isTeamViewer && (
              <div className="mt-3">
                <p className="text-xs text-muted-foreground mb-1">Vista de equipo: selecciona el cliente cuyo briefing quieres ver/editar.</p>
                <Select value={selectedClientId} onValueChange={setSelectedClientId}>
                  <SelectTrigger className="max-w-sm" data-testid="briefing-client-selector"><SelectValue placeholder="Selecciona un cliente..." /></SelectTrigger>
                  <SelectContent>{clientsList.map((c) => (<SelectItem key={c._id || c.id} value={c._id || c.id}>{c.nombre || c.name || 'Cliente'}</SelectItem>))}</SelectContent>
                </Select>
              </div>
            )}
          </CardHeader>
          <CardContent>
            {isTeamViewer && !selectedClientId ? (
              <div className="py-10 text-center text-muted-foreground" data-testid="briefing-no-client">Selecciona un cliente arriba para ver y editar su briefing.</div>
            ) : briefingData === null ? (
              <div className="py-10 text-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
            ) : (
              <BriefingForm
                key={selectedClientId || 'self'}
                readOnly={isClientViewMode()}
                data={briefingData}
                paymentMethod={briefingPayment}
                status={briefingStatus}
                onSetStatus={isClientViewMode() ? undefined : async (s) => {
                  try {
                    if (isTeamViewer) { await serviceDeliveryAPI.setBriefingStatus(selectedClientId, s); }
                    else { await clientPortalAPI.setBriefingStatus(s); }
                  } catch (e) { toast.error(e.message); return; }
                  setBriefingStatus(s);
                  toast.success(s === 'completado' ? '¡Briefing marcado como completado!' : 'Estado actualizado');
                }}
                onRemind={isTeamViewer ? async () => {
                  const res = await serviceDeliveryAPI.remindBriefing(selectedClientId);
                  const parts = []; if (res?.email_sent) parts.push('email'); if (res?.discord_sent) parts.push('Discord');
                  toast.success(parts.length ? `Recordatorio enviado por ${parts.join(' y ')}` : 'Recordatorio enviado');
                } : undefined}
                onUploadFile={(f) => (isTeamViewer ? serviceDeliveryAPI.uploadBriefingFile(selectedClientId, f) : clientPortalAPI.uploadBriefingFile(f))}
                buildFileUrl={briefingFileUrl}
                onSaveSection={async (sec, data) => {
                  if (isTeamViewer) { await serviceDeliveryAPI.saveBriefing(selectedClientId, { section: sec, data }); }
                  else { await clientPortalAPI.saveBriefing({ section: sec, data }); if (briefingStatus === 'pendiente') setBriefingStatus('en_proceso'); }
                  setBriefingData((prev) => ({ ...(prev || {}), [sec]: data }));
                }}
              />
            )}
          </CardContent>
        </Card>
      )}

      {/* ===== DOCUMENTOS / INFORMES (archivos: cliente solo Documentos; equipo también gestiona archivos de informes) ===== */}
      {(section === 'documents' || (section === 'reports' && isTeamViewer)) && (
        <>
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              {section === 'reports' ? <FileBarChart className="w-7 h-7 text-primary" /> : <FileText className="w-7 h-7 text-primary" />}
              {section === 'reports' ? 'Informes' : 'Documentos'}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {section === 'reports' ? 'Tus informes mensuales. Puedes verlos y descargarlos.' : 'Documentos que compartimos contigo (contratos, entregables).'}
            </p>
          </div>

          {section === 'documents' && !isTeamViewer && <ClientPlanFiles />}
          {section === 'documents' && !isTeamViewer && <ClientDriveFolders />}
          {section === 'documents' && !isTeamViewer && <ClientDocUpload onUploaded={loadDocs} />}

          {isTeamViewer && (
            <Card className="border-dashed">
              <CardContent className="p-4 space-y-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase">Gestión (equipo)</p>
                <Select value={selectedClientId} onValueChange={setSelectedClientId}>
                  <SelectTrigger className="max-w-sm" data-testid="doc-client-selector"><SelectValue placeholder="Selecciona un cliente..." /></SelectTrigger>
                  <SelectContent>{clientsList.map((c) => (<SelectItem key={c._id || c.id} value={c._id || c.id}>{c.nombre || c.name || 'Cliente'}</SelectItem>))}</SelectContent>
                </Select>
                {selectedClientId && (
                  <div className="flex flex-wrap items-end gap-2">
                    <input type="file" onChange={(e) => setUpFile(e.target.files?.[0] || null)} className="text-sm" data-testid="doc-upload-file" />
                    <input value={upNombre} onChange={(e) => setUpNombre(e.target.value)} placeholder="Nombre" className="border rounded-lg px-3 py-2 text-sm bg-background" data-testid="doc-upload-name" />
                    {section === 'reports' && <input type="month" value={upMes} onChange={(e) => setUpMes(e.target.value)} className="border rounded-lg px-3 py-2 text-sm bg-background" data-testid="doc-upload-month" />}
                    <Button size="sm" disabled={uploading} onClick={doUpload} data-testid="doc-upload-btn"><Upload className="w-4 h-4 mr-1" /> Subir</Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {docs === null ? (
            <div className="py-10 text-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
          ) : docs.length === 0 ? (
            <Card><CardContent className="py-14 text-center text-muted-foreground text-sm" data-testid="docs-empty">{isTeamViewer && !selectedClientId ? 'Selecciona un cliente.' : 'Aún no hay archivos aquí.'}</CardContent></Card>
          ) : (
            <Card><CardContent className="p-4 space-y-2">
              {docs.map((d) => (
                <div key={d.id} className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-lg border" data-testid={`doc-row-${d.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    {section === 'reports' ? <FileBarChart className="w-5 h-5 text-primary shrink-0" /> : <FileText className="w-5 h-5 text-primary shrink-0" />}
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{d.nombre}</p>
                      <p className="text-xs text-muted-foreground">{d.uploaded_by_client ? (isTeamViewer ? 'Subido por el cliente · ' : 'Subido por ti · ') : ''}{d.mes ? `${d.mes} · ` : ''}{d.created_at ? new Date(d.created_at).toLocaleDateString('es-ES') : ''}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <a href={clientPortalAPI.docDownloadUrl(d.id)} target="_blank" rel="noreferrer" data-testid={`doc-download-${d.id}`}>
                      <Button size="sm" variant="outline"><Download className="w-4 h-4 mr-1" /> Descargar</Button>
                    </a>
                    {isTeamViewer && <Button size="sm" variant="ghost" className="text-red-600" onClick={() => doDeleteDoc(d.id)} data-testid={`doc-delete-${d.id}`}><Trash2 className="w-4 h-4" /></Button>}
                  </div>
                </div>
              ))}
            </CardContent></Card>
          )}
        </>
      )}

      {/* ===== INFORMES GENERADOS (cliente): Contenido + Ads ===== */}
      {section === 'reports' && !isTeamViewer && <ClientReportsSection />}

      {/* ===== FACTURAS (Quipu) ===== */}
      {section === 'invoices' && (
        <>
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2"><Receipt className="w-7 h-7 text-primary" /> Facturas</h1>
            <p className="text-sm text-muted-foreground mt-1">Tus facturas. Puedes verlas y descargarlas en PDF.</p>
          </div>

          {isTeamViewer ? (
            <Card className="border-dashed">
              <CardContent className="p-4 space-y-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase">Vincular con Quipu (equipo)</p>
                <Select value={selectedClientId} onValueChange={setSelectedClientId}>
                  <SelectTrigger className="max-w-sm" data-testid="inv-client-selector"><SelectValue placeholder="Selecciona un cliente..." /></SelectTrigger>
                  <SelectContent>{clientsList.map((c) => (<SelectItem key={c._id || c.id} value={c._id || c.id}>{c.nombre || c.name || 'Cliente'}</SelectItem>))}</SelectContent>
                </Select>
                {selectedClientId && (
                  <div className="space-y-1.5">
                    <p className="text-xs text-muted-foreground flex items-center gap-1"><Link2 className="w-3.5 h-3.5" /> Contacto de Quipu de este cliente</p>
                    <Select value={linkedContactId} onValueChange={doLinkQuipu}>
                      <SelectTrigger className="max-w-sm" data-testid="quipu-contact-selector"><SelectValue placeholder="Selecciona el contacto de Quipu..." /></SelectTrigger>
                      <SelectContent>
                        {quipuContacts.map((qc) => {
                          const a = qc.attributes || {};
                          return <SelectItem key={qc.id} value={String(qc.id)}>{a.legal_name || a.name || a.commercial_name || `Contacto ${qc.id}`}</SelectItem>;
                        })}
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-muted-foreground">Una vez vinculado, el cliente verá SOLO las facturas de este contacto. Entra con la cuenta del cliente para comprobarlo.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : invoices === null ? (
            <div className="py-10 text-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
          ) : !invLinked ? (
            <Card><CardContent className="py-14 text-center text-muted-foreground text-sm" data-testid="inv-not-linked">Todavía no hemos vinculado tu facturación. Estará disponible en breve.</CardContent></Card>
          ) : invoices.length === 0 ? (
            <Card><CardContent className="py-14 text-center text-muted-foreground text-sm" data-testid="inv-empty">No tienes facturas por el momento.</CardContent></Card>
          ) : (
            <Card><CardContent className="p-0">
              <div className="divide-y">
                {invoices.map((inv) => {
                  const PAY = { paid: 'Pagada', due: 'Pendiente', overdue: 'Vencida', partially_paid: 'Pago parcial' };
                  const payLabel = PAY[inv.payment_status] || inv.payment_status || '';
                  const payColor = inv.payment_status === 'paid' ? 'text-emerald-600' : inv.payment_status === 'overdue' ? 'text-red-600' : 'text-amber-600';
                  return (
                  <div key={inv.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3" data-testid={`invoice-row-${inv.id}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-medium">Factura {inv.number}</p>
                      <p className="text-xs text-muted-foreground">{inv.issue_date || ''}{payLabel ? ' · ' : ''}<span className={payColor}>{payLabel}</span></p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="font-semibold">{inv.total != null ? `${Number(inv.total).toLocaleString('es-ES', { minimumFractionDigits: 2 })} ${inv.currency || 'EUR'}` : '—'}</span>
                      {inv.pdf_url ? (
                        <a href={inv.pdf_url} target="_blank" rel="noreferrer" data-testid={`invoice-download-${inv.id}`}><Button size="sm" variant="outline"><Download className="w-4 h-4 mr-1" /> PDF</Button></a>
                      ) : null}
                    </div>
                  </div>
                  );
                })}
              </div>
            </CardContent></Card>
          )}
        </>
      )}

      {/* ===== Secciones en preparación (Fase 4) ===== */}
      {/* ===== CAMPAÑAS (Meta) ===== */}
      {section === 'campaigns' && (
        <>
          <div className="flex items-end justify-between flex-wrap gap-3">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-2"><Megaphone className="w-7 h-7 text-primary" /> Campañas</h1>
              <p className="text-sm text-muted-foreground mt-1">El rendimiento de tus anuncios en Meta (Instagram y Facebook).</p>
            </div>
            {!isTeamViewer && (
              <input type="month" value={campMonth} onChange={(e) => setCampMonth(e.target.value)} className="border rounded-lg px-3 py-2 text-sm bg-background" data-testid="camp-month" />
            )}
          </div>

          {!isTeamViewer && <ClientAdsApprovals />}

          {isTeamViewer ? (
            <Card><CardContent className="py-14 text-center text-muted-foreground text-sm" data-testid="camp-team-note">
              Para que el cliente vea sus campañas, vincula su cuenta publicitaria de Meta desde <strong>Entrega de Servicios → ficha del cliente → Facturación y Anuncios</strong>.
            </CardContent></Card>
          ) : camp === null ? (
            <div className="py-10 text-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
          ) : !camp.linked ? (
            <Card><CardContent className="py-14 text-center text-muted-foreground text-sm" data-testid="camp-not-linked">No hay campañas activas por el momento.</CardContent></Card>
          ) : !camp.connected ? (
            <Card><CardContent className="py-14 text-center text-muted-foreground text-sm" data-testid="camp-not-connected">{camp.message || 'Tus campañas estarán disponibles muy pronto.'}</CardContent></Card>
          ) : (
            <>
              <StaleDataNote data={camp} testId="camp-stale-note" />
              {campHist && campHist.connected && (campHist.months || []).some((mo) => mo.spend > 0 || mo.leads > 0) && (
                <Card data-testid="camp-evolution-chart">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2"><TrendingUp className="w-4 h-4 text-primary" /> Evolución (últimos 6 meses)</CardTitle>
                    <p className="text-xs text-muted-foreground">Inversión vs. leads mes a mes.</p>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64 w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={campHist.months} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                          <XAxis dataKey="label" fontSize={12} tickLine={false} axisLine={false} />
                          <YAxis yAxisId="left" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `${v} €`} />
                          <YAxis yAxisId="right" orientation="right" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                          <Tooltip
                            formatter={(value, name) => name === 'Inversión' ? [`${Number(value).toLocaleString('es-ES', { minimumFractionDigits: 2 })} €`, name] : [Number(value).toLocaleString('es-ES'), name]}
                            contentStyle={{ borderRadius: 12, border: '1px solid #e5e7eb', fontSize: 12 }}
                          />
                          <Legend wrapperStyle={{ fontSize: 12 }} />
                          <Bar yAxisId="left" dataKey="spend" name="Inversión" fill="#10b981" radius={[4, 4, 0, 0]} barSize={28} />
                          <Line yAxisId="right" type="monotone" dataKey="leads" name="Leads" stroke="#6366f1" strokeWidth={2.5} dot={{ r: 3 }} />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              )}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="camp-summary">
                {(() => {
                  const s = camp.summary || {};
                  const cur = (camp.campaigns?.[0] && '€') || '€';
                  const cards = [
                    { icon: TrendingUp, label: 'Inversión', value: `${Number(s.spend || 0).toLocaleString('es-ES', { minimumFractionDigits: 2 })} ${cur}` },
                    { icon: Target, label: 'Leads', value: Number(s.leads || 0).toLocaleString('es-ES') },
                    { icon: Receipt, label: 'Coste por lead', value: s.leads ? `${Number(s.cost_per_lead || 0).toLocaleString('es-ES', { minimumFractionDigits: 2 })} ${cur}` : '—' },
                    { icon: Eye, label: 'Impresiones', value: Number(s.impressions || 0).toLocaleString('es-ES') },
                  ];
                  return cards.map((c) => (
                    <Card key={c.label}><CardContent className="p-4">
                      <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase font-semibold"><c.icon className="w-4 h-4" /> {c.label}</div>
                      <p className="text-2xl font-bold mt-1 tabular-nums">{c.value}</p>
                    </CardContent></Card>
                  ));
                })()}
              </div>

              <Card>
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><Megaphone className="w-4 h-4" /> Tus campañas</CardTitle></CardHeader>
                <CardContent className="p-0">
                  {(!camp.campaigns || camp.campaigns.length === 0) ? (
                    <p className="text-sm text-muted-foreground text-center py-10" data-testid="camp-empty">No hay campañas con actividad este mes.</p>
                  ) : (
                    <div className="divide-y">
                      {camp.campaigns.map((c) => (
                        <div key={c.id} className="px-4 py-3 flex flex-wrap items-center justify-between gap-3" data-testid={`campaign-row-${c.id}`}>
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">{c.name}</p>
                            {c.objetivo && (
                              <p className="text-xs text-primary flex items-center gap-1 mt-0.5" data-testid={`campaign-objetivo-${c.id}`}><Target className="w-3 h-3 shrink-0" /> <span className="truncate">Objetivo: {c.objetivo}</span></p>
                            )}
                            <p className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
                              <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3" /> {Number(c.impressions || 0).toLocaleString('es-ES')}</span>
                              <span className="inline-flex items-center gap-1"><MousePointerClick className="w-3 h-3" /> {Number(c.clicks || 0).toLocaleString('es-ES')}</span>
                            </p>
                          </div>
                          <div className="flex items-center gap-4 shrink-0 text-right">
                            <div><p className="text-[11px] text-muted-foreground uppercase">Leads</p><p className="font-semibold tabular-nums">{Number(c.leads || 0).toLocaleString('es-ES')}</p></div>
                            <div><p className="text-[11px] text-muted-foreground uppercase">Inversión</p><p className="font-semibold tabular-nums">{Number(c.spend || 0).toLocaleString('es-ES', { minimumFractionDigits: 2 })} €</p></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </>
      )}

      {/* Dialogs */}
      <AlertDialog open={!!confirmPost} onOpenChange={(open) => !open && setConfirmPost(null)}>
        <AlertDialogContent data-testid="approve-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Aprobar publicación?</AlertDialogTitle>
            <AlertDialogDescription>Vas a aprobar <strong>{confirmPost?.titulo || 'esta publicación'}</strong>. Pasará a programación y ya no podrás editarla. ¿Continuar?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="approve-cancel-btn">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleApprove(confirmPost)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="approve-confirm-btn">Sí, aprobar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!changesPost} onOpenChange={(open) => { if (!open) { setChangesPost(null); setChangesText(''); } }}>
        <AlertDialogContent data-testid="request-changes-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Solicitar cambios</AlertDialogTitle>
            <AlertDialogDescription>Describe qué quieres cambiar de <strong>{changesPost?.titulo || 'esta publicación'}</strong>. El equipo lo recibirá y se pondrá a ello.</AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea value={changesText} onChange={(e) => setChangesText(e.target.value)} placeholder="Ej: cambiar la música del reel y corregir el precio del copy..." rows={5} className="resize-none" data-testid="request-changes-textarea" />
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="request-changes-cancel">Cancelar</AlertDialogCancel>
            <Button onClick={handleRequestChanges} disabled={submittingChanges || !changesText.trim()} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="request-changes-submit">
              {submittingChanges ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enviando...</>) : (<><Send className="w-4 h-4 mr-2" /> Enviar observaciones</>)}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  );
}

function HomeDriveCard({ onOpen }) {
  const folders = useClientDriveFolders(null, false);
  if (!folders || folders.length === 0) return null;
  return (
    <Card className="cursor-pointer hover:border-primary transition-colors" onClick={onOpen} data-testid="home-drive-card">
      <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <FolderOpen className="w-5 h-5 text-primary" />
          <div>
            <p className="font-medium text-sm">Tus carpetas de Drive</p>
            <p className="text-xs text-muted-foreground">{folders.length} {folders.length === 1 ? 'carpeta compartida' : 'carpetas compartidas'} · {folders.map((f) => f.nombre).slice(0, 3).join(', ')}{folders.length > 3 ? '…' : ''}</p>
          </div>
        </div>
        <Button variant="outline" size="sm" data-testid="home-drive-btn">Ver carpetas <ArrowRight className="w-3.5 h-3.5 ml-1" /></Button>
      </CardContent>
    </Card>
  );
}
